import type { JSX } from 'react';
import { Seo } from '@/components/seo';
import { ActionCard } from '@/components/dashboard/ActionCard';
import { DashboardOverviewStats } from '@/components/dashboard/overview/DashboardOverviewStats';
import { DashboardActionQueue } from '@/components/dashboard/overview/DashboardActionQueue';
import { EnvelopeOpenIcon, PlusCircledIcon, ChatBubbleIcon } from '@radix-ui/react-icons';

export function AdminDashboard(): JSX.Element {
  return (
    <section className="flex flex-col gap-xl py-8" aria-labelledby="admin-dashboard-title">
      <Seo
        title="Administration & Leasing Oversight"
        description="Monitor vacancy rates, campaign performance, and approvals for Strashin Developments. Sign in for updates as leasing analytics and governance tools roll out."
        canonicalPath="/dashboard"
        noIndex
        openGraph={{ image: '/logo.jpg' }}
      />
      <header className="flex flex-col gap-4">
        <p className="text-xs font-semibold uppercase tracking-[0.4em] text-brand-accent">
          Portfolio Oversight
        </p>
        <h1
          id="admin-dashboard-title"
          className="text-3xl font-semibold text-brand-primary md:text-4xl"
        >
          Administration & Leasing Oversight
        </h1>
        <p className="max-w-3xl text-sm text-text-primary/70 md:text-base">
          Configure leasing campaigns, monitor vacancy health, and govern platform access. We're
          preparing dashboards for vacancy absorption, lead sources, and approvals so you can audit
          the entire portfolio in one place.
        </p>
      </header>

      <DashboardOverviewStats />
      <DashboardActionQueue />

      <div className="grid gap-4 md:grid-cols-3">
        <ActionCard
          title="Capture lead"
          description="Log a new prospect and assign a follow-up sequence before momentum slips."
          to="/dashboard/leads"
          icon={EnvelopeOpenIcon}
          badge="Pipeline"
          ctaLabel="Open leads"
        />
        <ActionCard
          title="Add unit"
          description="Choose a property and add a new suite with pricing and availability details."
          to="/dashboard/properties"
          icon={PlusCircledIcon}
          badge="Inventory"
          ctaLabel="Add unit"
        />
        <ActionCard
          title="Review properties"
          description="Audit property inventory, readiness, and publishing status across the portfolio."
          to="/dashboard/properties"
          icon={ChatBubbleIcon}
          badge="Portfolio"
          ctaLabel="Open properties"
        />
      </div>
    </section>
  );
}
